import React, { useCallback, useState } from 'react';
import GroupToggleLabel from './GroupToggleLabel';
import { TAP_TARGET_PHONE } from '../../constants/buttons';

/**
 * THE collapsible GROUPED LIST — collapse state, Expand all / Collapse all and
 * ONE group header, for every grouped list. Extracted 2026-10-02
 * (Gags/teams/tasks Stage 1 reuse plan: *"extract a ui/GroupedList
 * (collapsible groups + Expand/Collapse all + the shared header with counts),
 * used by My work and Given by me now; the Breakdown adopts it when next
 * touched"*) out of the My Tasks page, which had re-implemented it inline.
 *
 * Three exports, because the SAME state drives two different DOMs — a
 * `<tr>`-per-header desktop table (`ui/PageScrollTable` rows) and a phone card
 * list — and a `<div>` wrapper cannot sit inside a `<tbody>`:
 *  - `useGroupedList({ defaultOpen, forceOpen })` — the state. `isOpen(group)`
 *    answers from an explicit toggle, else `forceOpen(group)` (e.g. the group
 *    holding the expanded row), else `defaultOpen`. A group whose `label` is
 *    `null` is UNTITLED (a flat list) and always open. `reset()` drops every
 *    toggle — call it when the grouping or the search changes.
 *  - `GroupHeader` — the toggle (chevron + label via `ui/GroupToggleLabel`) +
 *    an optional summary node, with optional `actions` as a SIBLING of the
 *    toggle (never nested inside it — a click there must not also toggle).
 *  - `ExpandAllToggle` — one ghost control, rendered only when there is more
 *    than one titled group.
 *  - default `GroupedList` — the card-list layout, for a phone.
 * Session state only; nothing is stored (the Breakdown ruling, 2026-09-22).
 */
export function useGroupedList({ defaultOpen = false, forceOpen = null } = {}) {
  const [toggled, setToggled] = useState({});
  const isOpen = useCallback((group) => {
    if (!group || group.label == null) return true;
    if (forceOpen && forceOpen(group)) return true;
    return Object.prototype.hasOwnProperty.call(toggled, group.key) ? toggled[group.key] : defaultOpen;
  }, [toggled, defaultOpen, forceOpen]);
  const toggle = useCallback((group) => {
    setToggled((prev) => {
      const current = Object.prototype.hasOwnProperty.call(prev, group.key) ? prev[group.key] : defaultOpen;
      return { ...prev, [group.key]: !current };
    });
  }, [defaultOpen]);
  const setAll = useCallback((groups, open) => {
    setToggled((prev) => {
      const next = { ...prev };
      groups.filter((g) => g.label != null).forEach((g) => { next[g.key] = open; });
      return next;
    });
  }, []);
  const reset = useCallback(() => setToggled({}), []);
  return { isOpen, toggle, setAll, reset };
}

/**
 * `stacked` (opt-in, default off): the PHONE header — exactly TWO lines, the
 * label on line 1 and the summary on line 2, each ellipsed rather than wrapped
 * (owner, Gags/teams/tasks fix round 2026-10-02: "name, then counts"). Off, the
 * header is byte-identical to the desktop one.
 */
export function GroupHeader({ open, onToggle, label, summary, actions, testId, className = '', stacked = false }) {
  return (
    // 🎨 bg-gray-100 (owner 2026-10-03: "a slightly different header shade so
    // we can tell it's a header") — one step darker than the rows' white.
    // 🎨 slate-200/60: a cool tint distinct from BOTH the white rows and the
    // gray-100 page behind collapsed groups (owner, second pass same day).
    <div className={`flex items-center gap-2 bg-slate-200/60 dark:bg-slate-700/80 ${className}`} data-testid={testId}>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        data-testid={testId ? `${testId}-toggle` : undefined}
        className={stacked
          // 📏 Two tight lines (owner 2026-10-03: "the header should be less
          // tall than the tasks"): py-1, no gap, 44px tap floor kept.
          ? 'flex-1 min-w-0 flex flex-col items-stretch gap-0 text-left px-3 py-1 min-h-[44px] leading-tight focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
          : 'flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-left px-3 py-2 min-h-[44px] md:min-h-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'}
      >
        {stacked ? (
          <>
            <span className="flex items-center gap-2 min-w-0">
              <GroupToggleLabel open={open} label={label} truncate />
            </span>
            {summary && (
              <span className="block pl-6 truncate" data-testid={testId ? `${testId}-line2` : undefined}>{summary}</span>
            )}
          </>
        ) : (
          <>
            <GroupToggleLabel open={open} label={label} />
            {summary}
          </>
        )}
      </button>
      {actions}
    </div>
  );
}

export function ExpandAllToggle({ groups, isOpen, setAll, testId }) {
  const titled = groups.filter((g) => g.label != null);
  if (titled.length < 2) return null;
  const anyClosed = titled.some((g) => !isOpen(g));
  return (
    <button
      type="button"
      onClick={() => setAll(groups, anyClosed)}
      data-testid={testId}
      className={`px-2 py-1 text-xs font-semibold text-indigo-700 dark:text-indigo-300 hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${TAP_TARGET_PHONE}`}
    >
      {anyClosed ? 'Expand all' : 'Collapse all'}
    </button>
  );
}

/**
 * The card layout (a phone): one bordered card per group, header on top, the
 * caller's items below while open. `renderHeaderExtras(group)` →
 * `{ summary, actions }`; `renderItems(group)` → the rows.
 */
export default function GroupedList({ groups, state, renderItems, renderHeaderExtras, testIdPrefix }) {
  return (
    <div className="space-y-1" data-testid={testIdPrefix ? `${testIdPrefix}-groups` : undefined}>
      {groups.map((group) => {
        const open = state.isOpen(group);
        const extras = renderHeaderExtras ? renderHeaderExtras(group) : {};
        return (
          <div
            key={group.key}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm"
            data-testid={testIdPrefix && group.label != null ? `${testIdPrefix}-group-${group.key}` : undefined}
          >
            {group.label != null && (
              <GroupHeader
                open={open}
                onToggle={() => state.toggle(group)}
                label={group.label}
                summary={extras.summary}
                actions={extras.actions}
                testId={testIdPrefix ? `${testIdPrefix}-group-header-${group.key}` : undefined}
              />
            )}
            {open && (
              <div className={`divide-y divide-gray-200 dark:divide-gray-700 ${group.label != null ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}>
                {renderItems(group)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
